import {
  Component,
  EventEmitter,
  HostListener,
  Inject,
  Output,
  Renderer2,
  ChangeDetectorRef,
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ImageService } from 'src/app/common/services/image/image.service';
import { ScriptService } from 'src/app/common/services/load.script/load.script.service';

import jwt_decode from 'jwt-decode';
import { SocialLoginComponent } from '../social.login.component';
import { BaseComponent } from 'src/app/common/components/base.component';
import { ActivatedRoute } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { CheckLoginStatusService } from '../checkLoginStatus.service';
import { PostMessageService } from '../post-message.service';

@Component({
  selector: 'ig-google-login',
  templateUrl: './google.login.component.html',
  styleUrls: ['./google.login.component.scss'],
})
export class GoogleLoginComponent extends BaseComponent {
  public auth2: any;
  public profilePicture: string;
  public static isUserLoggedIn: boolean;
  public static googleUserId: string;
  public static profilePicture: string;
  public static email: string;
  public static userName: string;

  private isGoogleInitialized: boolean = false;
  private messageListener: (event: MessageEvent) => void;

  SCRIPT_PATH = `https://accounts.google.com/gsi/client`;

  // notify SocialLoginComponent when google library is loaded, then show Facebook login button too
  public static googleReady$ = new BehaviorSubject(false);

  // Output event for login status changes
  @Output() onLoginStatusChanged = new EventEmitter<boolean>();

  constructor(
    public activatedRoute: ActivatedRoute,
    public imageService: ImageService,
    public scriptService: ScriptService,
    private _renderer: Renderer2,
    private checkLoginStatusService: CheckLoginStatusService,
    private changeDetector: ChangeDetectorRef,
    private postMessageService: PostMessageService,
    @Inject(DOCUMENT) private _document: Document
  ) {
    super({ activatedRoute });

    this.checkInitialLoginStatus();

    // Load Google library on component initialization
    this.loadGoogleLibrary();
  }

   public loadGoogleLibrary() {
    // Only skip SDK loading if in a WebView AND a native bridge exists
    if (this.isInWebView() && this.postMessageService.hasNativeBridge()) {
      GoogleLoginComponent.googleReady$.next(true);
      return;
    }

    if ((window as any).google?.accounts?.id) {
      GoogleLoginComponent.googleReady$.next(true);
      this.initializeGoogle();
      return;
    }

    if (!this.isGsiScriptAlreadyPresent()) {
      const scriptElement = this.scriptService.loadJsScript(
        this._renderer,
        this.SCRIPT_PATH
      );
      scriptElement.onload = () => {
        GoogleLoginComponent.googleReady$.next(true);
        this.initializeGoogle();
      };
      scriptElement.onerror = () => {
        console.error('Could not load the script: ' + this.SCRIPT_PATH);
        // Still mark ready so Facebook button can show
        GoogleLoginComponent.googleReady$.next(true);
      };
    } else {

      this.waitForGsi(() => {
        GoogleLoginComponent.googleReady$.next(true);
        this.initializeGoogle();
      });
    }
  }

  private isGsiScriptAlreadyPresent(): boolean {
    if (this.scriptService.isScriptExist(this.SCRIPT_PATH)) {
      return true;
    }
    // Match src with any query string (imgoingscript.js appends ?v=<random>)
    const scripts = this._document.getElementsByTagName('script');
    for (let i = 0; i < scripts.length; i++) {
      const src = scripts[i].getAttribute('src') || '';
      if (src.split('?')[0] === this.SCRIPT_PATH) {
        return true;
      }
    }
    return false;
  }

  private waitForGsi(cb: () => void, attempt = 0) {
    if ((window as any).google?.accounts?.id) {
      cb();
      return;
    }
    if (attempt > 30) {
      GoogleLoginComponent.googleReady$.next(true);
      return;
    }
    setTimeout(() => this.waitForGsi(cb, attempt + 1), 500);
  }

  @HostListener('window:onGoogleLibraryLoad')
  public initializeGoogle() {
    const hasBridge = this.postMessageService.hasNativeBridge();

    if (this.isGoogleInitialized) {
      setTimeout(() => {
        if (this.isInWebView() && hasBridge) {
          this.renderCustomGoogleButton();
        } else {
          this.renderGoogleButtons();
        }
      }, 100);
      return;
    }

    // WebView with native bridge: use custom button that sends via bridge
    if (this.isInWebView() && hasBridge) {
      this.checkInitialLoginStatus();
      setTimeout(() => this.renderCustomGoogleButton(), 200);
      return;
    }

    // Browser or WebView-without-bridge: use Google SDK
    const google = (window as any).google;
    if (google?.accounts?.id?.initialize) {
      setTimeout(() => this.performGoogleInitialization(), 150);
    } else if (!this.isGsiScriptAlreadyPresent()) {
      this.loadGoogleLibrary();
      const checkInterval = setInterval(() => {
        if ((window as any).google?.accounts?.id?.initialize) {
          clearInterval(checkInterval);
          this.performGoogleInitialization();
        }
      }, 100);
      setTimeout(() => clearInterval(checkInterval), 10000);
    } else {
      this.waitForGsi(() => this.performGoogleInitialization());
    }
  }

  // Make public so it can be called from parent component after DOM updates
  public renderCustomGoogleButton() {
    // For webview, create a custom button that looks like Google's
    // Check both component's document and global document (for popup modals)
    const localContainers = (this.shadowRoot || this._document).querySelectorAll(
      '.google-signin-btn'
    );
    const globalContainers = this._document.querySelectorAll(
      '.google-signin-btn'
    );
    // Combine both sets and remove duplicates
    const allContainers = Array.from(localContainers).concat(Array.from(globalContainers));
    const uniqueContainers = Array.from(new Set(allContainers));

    // Check if popup is open
    const isPopupOpen = this._document.querySelector('.login-popup-overlay') !== null;

    // Filter out hidden containers
    let visibleContainers = uniqueContainers.filter((container: Element) => {
      // Skip if container is not in DOM
      if (!container || !container.parentElement) return false;
      
      // Check if parent has ig-hide class or is hidden
      const parent = container.closest('.ig-hide');
      if (parent) return false;
      
      // Check if element itself is hidden
      const style = window.getComputedStyle(container as HTMLElement);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
      
      // Check if container is in a popup
      const isInPopup = container.closest('.login-popup-content') !== null;
      
      // If it's in a popup, only render if popup is open
      if (isInPopup && !isPopupOpen) return false;
      
      return true;
    });
    
    // If popup is open, prioritize containers inside the popup
    if (isPopupOpen) {
      const popupContainers = visibleContainers.filter((container: Element) => 
        container.closest('.login-popup-content') !== null
      );
      // If we found containers in the popup, use those; otherwise use all visible containers
      if (popupContainers.length > 0) {
        visibleContainers = popupContainers;
      }
    }

    visibleContainers.forEach((container) => {
      // Skip if already has a button (custom or SDK)
      if (!container.querySelector('.custom-google-btn') && !container.querySelector('.abcRioButton')) {
        const button = this._document.createElement('button');
        button.className = 'custom-google-btn';
        button.innerHTML = `
          <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" style="margin-right: 8px; vertical-align: middle;">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          <span style="font-family: 'Roboto', arial, sans-serif; font-size: 14px; color: #3c4043; font-weight: 500;">Sign in with Google</span>
        `;
        button.style.cssText = `
          background-color: #fff;
          border: 1px solid #dadce0;
          border-radius: 4px;
          padding: 11px 24px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-family: 'Roboto', arial, sans-serif;
          transition: background-color .218s, border-color .218s;
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
        `;
        
        button.onmouseover = () => {
          button.style.backgroundColor = '#f8f9fa';
          button.style.borderColor = '#dadce0';
        };
        
        button.onmouseout = () => {
          button.style.backgroundColor = '#fff';
          button.style.borderColor = '#dadce0';
        };
        
        // Use arrow function to preserve 'this' context
        button.onclick = (e: MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          // Only send login request when user explicitly clicks the button
          if (this.postMessageService && this.postMessageService.isInWebView()) {
            this.postMessageService.sendLoginRequest('google');
          }
        };
        
        container.appendChild(button);
      }
    });
  }

  private performGoogleInitialization() {
    // Prevent multiple initializations
    if (this.isGoogleInitialized) {
      this.renderGoogleButtons();
      return;
    }

    // check if user is already logged in from local storage
    const googleUser = localStorage.getItem('social.login.googleUser');
    if (googleUser) {
      const googleUserObj = JSON.parse(googleUser);
      GoogleLoginComponent.isUserLoggedIn = true;
      GoogleLoginComponent.googleUserId = googleUserObj.userId;
      GoogleLoginComponent.profilePicture = googleUserObj.profilePicture;
      GoogleLoginComponent.email = googleUserObj.email;
      GoogleLoginComponent.userName = googleUserObj.userName;
      if (!SocialLoginComponent.loginStatus$.getValue()) {
        SocialLoginComponent.loginStatus$.next(true);
      }

      // Emit login status change event
      this.onLoginStatusChanged.emit(true);
    }

    const google = (window as any).google;

    if (google?.accounts?.id?.initialize) {
      try {
        /**
         * Initialize Google Sign-In SDK
         * 
         * NOTE: Cross-Origin-Opener-Policy (COOP) Warnings
         * 
         * You may see console warnings like:
         * "Cross-Origin-Opener-Policy policy would block the window.postMessage call"
         * 
         * These warnings come from Google's SDK itself when it tries to communicate
         * with its OAuth popup windows. They are:
         * - Harmless: The login flow still works correctly
         * - Expected: Common with third-party auth services (Google, Auth0, etc.)
         * - Not errors: The SDK handles this gracefully and falls back to redirect flow if needed
         * 
         * To minimize these warnings:
         * 1. Ensure your server sets: Cross-Origin-Opener-Policy: same-origin-allow-popups
         * 2. Or use: Cross-Origin-Opener-Policy: restrict-properties (newer, more secure)
         * 
         * See: https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Cross-Origin-Opener-Policy
         */
        if ((window as any).__igGisInitialized) {
          this.isGoogleInitialized = true;
          setTimeout(() => this.renderGoogleButtons(), 100);
          return;
        }

        google['accounts'].id.initialize({
          client_id: '665289826545-463bhl103plveo5l05b94vjnenmr3f7e',
          auto_select: false,
          cancel_on_tap_outside: true,
          itp_support: true,
          callback: (data) => {
            if (data['credential']) {
              const credential = jwt_decode(data['credential']);
              this.profilePicture = credential['picture'];
              GoogleLoginComponent.isUserLoggedIn = true;
              GoogleLoginComponent.googleUserId = credential['email'] + '|google';
              GoogleLoginComponent.email = credential['email'];
              GoogleLoginComponent.userName = credential['name'];
              GoogleLoginComponent.profilePicture = this.profilePicture;

              // Save to localStorage
              localStorage.setItem(
                'social.login.googleUser',
                JSON.stringify({
                  userId: GoogleLoginComponent.googleUserId,
                  profilePicture: GoogleLoginComponent.profilePicture,
                  email: GoogleLoginComponent.email,
                  userName: GoogleLoginComponent.userName,
                })
              );
              this.checkLoginStatusService.checkGoogleLoginStatus();
              this.sendLoginSuccessInfo();
              this.onLoginStatusChanged.emit(true);
            }
          },
        });

        this.isGoogleInitialized = true;
        (window as any).__igGisInitialized = true;
        setTimeout(() => {
          this.renderGoogleButtons();
        }, 100);
      } catch (error) {
        // Log initialization errors, but note that COOP warnings from Google's SDK
        // are expected and don't prevent the login flow from working
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (errorMessage.includes('Cross-Origin-Opener-Policy')) {
          // This is a known issue with Google's SDK - it's a warning, not a fatal error
          console.warn(
            '[GoogleLogin] COOP warning detected. This is expected behavior from Google\'s SDK. ' +
            'Login functionality will still work. To minimize warnings, ensure your server sets ' +
            'Cross-Origin-Opener-Policy: same-origin-allow-popups header.'
          );
        } else {
          console.error('Failed to initialize Google Sign-In:', error);
        }
      }
    }
  }

  // Public method to render button in a specific element
  public renderButtonInElement(element: HTMLElement): void {
    const google = (window as any).google;

    if (!google?.accounts?.id?.renderButton) {
      console.warn('[GoogleLogin] GIS not ready – cannot render button');
      return;
    }

    if (element && !element.querySelector('.abcRioButton')) {
      try {
        const containerWidth = element.offsetWidth;
        const fallbackWidth = window.innerWidth >= 400 ? 295 : 260;
        const width = containerWidth > 0 ? Math.min(Math.floor(containerWidth), 400) : fallbackWidth;
        google.accounts.id.renderButton(element, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          width,
          text: 'signin_with',
          shape: 'rectangular',
        });
      } catch (error) {
        console.error('Failed to render Google button in provided element:', error);
      }
    }
  }  

  private renderGoogleButtons(retryCount = 0): void {
    const google = (window as any).google;

    if (!google?.accounts?.id?.renderButton) {
      console.warn('[GoogleLogin] GIS not ready – skip renderButton() call');
      return;
    }

    // Find all Google sign-in button containers
    const loginBtnItems = (this.shadowRoot || this._document).querySelectorAll(
      '.google-signin-btn'
    );

    const loginBtnItemsInPopup = this._document.querySelectorAll(
      '.cdk-overlay-container .google-signin-btn'
    );

    // Also check for buttons in the login popup modal
    const loginBtnItemsInModal = this._document.querySelectorAll(
      '.login-popup .google-signin-btn'
    );

    // Check for the specific popup button container
    const popupButtonContainer = this._document.querySelector(
      '#google-signin-btn-popup'
    );

    const allButtons = Array.from(loginBtnItems)
      .concat(Array.from(loginBtnItemsInPopup))
      .concat(Array.from(loginBtnItemsInModal));

    // Add the specific popup container if it exists
    if (popupButtonContainer) {
      allButtons.push(popupButtonContainer);
    }

    // Remove duplicates and filter out hidden/invalid containers
    const uniqueButtons = Array.from(new Set(allButtons)).filter((el: Element) => {
      // Skip if container is hidden or not in DOM
      if (!el || !el.parentElement) return false;
      
      // Check if parent has ig-hide class or is hidden
      const parent = el.closest('.ig-hide');
      if (parent) return false;
      
      // Check if element itself is hidden
      const style = window.getComputedStyle(el as HTMLElement);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
      
      return true;
    });

    uniqueButtons.forEach((el) => {
      if (!el.querySelector('.abcRioButton') && !el.querySelector('.custom-google-btn')) {
        const containerWidth = (el as HTMLElement).offsetWidth;
        const fallbackWidth = window.innerWidth >= 400 ? 295 : 260;
        const width = containerWidth > 0 ? Math.min(Math.floor(containerWidth), 400) : fallbackWidth;
        google.accounts.id.renderButton(el, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          width,
          text: 'signin_with',
          shape: 'rectangular',
        });
      }
    });
  }

  // Static method to update Google login status from app-level OAuth
  public static updateFromAppLogin(loginData: any) {
    if (loginData.isLoggedIn && loginData.provider === 'google') {
      GoogleLoginComponent.isUserLoggedIn = true;
      GoogleLoginComponent.googleUserId = loginData.userId;
      GoogleLoginComponent.email = loginData.email;
      GoogleLoginComponent.userName = loginData.userName;
      GoogleLoginComponent.profilePicture = loginData.profilePicture;

      // Store in localStorage for compatibility with existing code
      localStorage.setItem(
        'social.login.googleUser',
        JSON.stringify({
          userId: loginData.userId,
          profilePicture: loginData.profilePicture,
          email: loginData.email,
          userName: loginData.userName,
        })
      );

      if (!SocialLoginComponent.loginStatus$.getValue()) {
        SocialLoginComponent.loginStatus$.next(true);
      }
    } else {
      GoogleLoginComponent.clearLoginData();
    }
  }

  // Static method to clear Google login data
  public static clearLoginData() {
    GoogleLoginComponent.isUserLoggedIn = false;
    GoogleLoginComponent.googleUserId = null;
    GoogleLoginComponent.profilePicture = null;
    GoogleLoginComponent.email = null;
    GoogleLoginComponent.userName = null;
    localStorage.removeItem('social.login.googleUser');
    if (SocialLoginComponent.loginStatus$.getValue()) {
      SocialLoginComponent.loginStatus$.next(false);
    }
  }

  // Check if we're in a webview
  private isInWebView(): boolean {
    return (
      window.parent !== window ||
      navigator.userAgent.includes('wv') || // Android WebView
      (navigator.userAgent.includes('Version/') &&
        navigator.userAgent.includes('Mobile/'))
    ); // iOS WebView
  }

  // Request app-level Google login (only when user clicks button)
  private requestAppLogin() {
    if (this.postMessageService.isInWebView()) {
      this.postMessageService.sendLoginRequest('google');
    }
  }

  // Request app-level Google logout
  private requestAppLogout() {
    if (this.postMessageService.isInWebView()) {
      this.postMessageService.sendLogoutRequest('google');
    }
  }

  // Send user info after successful login (always send if in WebView/iframe context)
  private sendLoginSuccessInfo() {
    // Send user info via postMessage if in WebView/iframe context
    if (this.postMessageService.isInWebView() && GoogleLoginComponent.isUserLoggedIn) {
      this.postMessageService.sendLoginSuccess({
        provider: 'google',
        userId: GoogleLoginComponent.googleUserId,
        email: GoogleLoginComponent.email,
        userName: GoogleLoginComponent.userName,
        profilePicture: GoogleLoginComponent.profilePicture,
      });
    }
  }

  public login() {
    // In WebView, send login request when user clicks
    if (this.isInWebView()) {
      this.requestAppLogin();
      return;
    }
    
    // In browser, trigger Google's button click
    const google = (window as any).google;
    if (google?.accounts?.id) {
      // Find and click the Google button
      const popupContainer = document.querySelector('#google-signin-btn-popup');
      const buttonContainers = document.querySelectorAll('.google-signin-btn');
      
      // Try popup container first
      if (popupContainer) {
        const popupButton = popupContainer.querySelector('.abcRioButton') as HTMLElement;
        if (popupButton) {
          popupButton.click();
          return;
        }
      }
      
      // Fallback to regular containers
      buttonContainers.forEach(container => {
        const googleButton = container.querySelector('.abcRioButton') as HTMLElement;
        if (googleButton) {
          googleButton.click();
        }
      });
    }
  }

  public go() {
    // Development/testing method - keep as is
    GoogleLoginComponent.isUserLoggedIn = true;
    GoogleLoginComponent.googleUserId = '117302739800456246841|google';
    GoogleLoginComponent.profilePicture =
      'https://lh3.googleusercontent.com/a/AGNmyxbLIc5wFVwhFt0S3nko5iUrXK4tCh1gLndHliWk=s96-c';
    SocialLoginComponent.loginStatus$.next(true);
  }

  public logout(): void {
    /* ---- 1. If the component is embedded in your mobile-app web-view ---- */
    if (this.isInWebView()) {
      // tell the container app to sign the user out
      this.requestAppLogout();
      GoogleLoginComponent.clearLoginData();
      // Send logout success confirmation after clearing data
      if (this.postMessageService.isInWebView()) {
        this.postMessageService.sendLogoutSuccess();
      }
      return; // <-- do NOT fall through
    }

    /* ---- 2. Regular browser tab ---- */
    // Only touch the GIS SDK when it is really present
    const g = (window as any).google;
    if (g?.accounts?.id?.disableAutoSelect) {
      g.accounts.id.disableAutoSelect();
    }

    GoogleLoginComponent.clearLoginData();
    
    // Send logout success for browser logout too (if in iframe context)
    if (this.postMessageService.isInWebView()) {
      this.postMessageService.sendLogoutSuccess();
    }
  }

  public get isUserLoggedWithGoogle() {
    return GoogleLoginComponent.isUserLoggedIn;
  }

  // Method to check login status on component initialization
  public checkInitialLoginStatus() {
    // Check if there's existing login data in localStorage
    const storedGoogleUser = localStorage.getItem('social.login.googleUser');
    if (storedGoogleUser) {
      try {
        const userData = JSON.parse(storedGoogleUser);
        GoogleLoginComponent.isUserLoggedIn = true;
        GoogleLoginComponent.googleUserId = userData.userId;
        GoogleLoginComponent.profilePicture = userData.profilePicture;
        GoogleLoginComponent.email = userData.email;
        GoogleLoginComponent.userName = userData.userName;
        SocialLoginComponent.loginStatus$.next(true);
        
        // Emit login status change event
        this.onLoginStatusChanged.emit(true);
      } catch (e) {
        console.warn('Error parsing stored Google user data:', e);
        localStorage.removeItem('social.login.googleUser');
      }
    }
  }
}
